import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { Brand, Branch } from '../../core/models/brand.model';
import { Address, Area } from '../../core/models/address.model';
import { FulfillmentMode } from '../../core/models/order.model';
import { DataService } from '../../core/services/data.service';
import { SessionService } from '../../core/services/session.service';
import { UiService } from '../../core/services/ui.service';
import { ConfigService } from '../../core/services/config.service';
import { StorefrontService } from '../../core/services/storefront.service';
import { AccountService } from '../../core/services/account.service';
import { BranchService, mapOrderType } from '../../core/services/branch.service';
import { I18nService } from '../../core/services/i18n.service';
import { normalizeEgPhone, PHONE_DIGITS } from '../../core/services/phone.util';
import { SheetComponent, BtnComponent, IconComponent, SpinnerComponent, MapPickerComponent, MAP_DEFAULT_CENTER, TranslatePipe } from '../../shared';

/** A branch row for the Pickup/Dine-in list. */
interface BranchView {
  id: string; name: string; eta: number | null; scheduling: boolean;
  address?: string; dist?: number; open?: boolean; opensAt?: string | null; nearest?: boolean;
}
/** An address row for the Delivery list. */
interface AddrView { id: string; label: string; line: string; branchId: string; distanceKm?: number; isDefault?: boolean; real?: boolean; }

@Component({
  selector: 'app-fulfillment-sheet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, SheetComponent, BtnComponent, IconComponent, SpinnerComponent, MapPickerComponent, TranslatePipe],
  templateUrl: './fulfillment-sheet.component.html',
  styles: [`
    .bb-f-lbl { font-size: 12.5px; font-weight: 700; color: var(--fr-text-2); display: block; margin: 12px 0 7px; }
    .bb-f-input {
      width: 100%; box-sizing: border-box; height: 50px; border-radius: 13px;
      border: 1.5px solid var(--fr-border-2); outline: none; padding: 0 14px; font-size: 15px;
      font-family: var(--fr-font); background: var(--fr-surface); color: var(--fr-text);
    }
    .bb-f-act { border: none; background: none; cursor: pointer; color: var(--brand-deep); font-weight: 700; font-size: 12.5px; font-family: var(--fr-font); display: inline-flex; align-items: center; gap: 4px; padding: 0; }
  `],
})
export class FulfillmentSheetComponent implements OnInit, OnDestroy {
  open = false;
  brand!: Brand;

  private readonly allModes: [FulfillmentMode, string, string][] = [
    ['delivery', 'Delivery', 'solar:scooter-bold'],
    ['pickup', 'Pickup', 'solar:bag-3-bold'],
    ['dinein', 'Dine-in', 'solar:tablet-bold'],
  ];
  get modes(): [FulfillmentMode, string, string][] {
    const supported = this.config.supportedOrderTypes;
    return this.allModes.filter((m) => supported.includes(m[0]));
  }
  /** The getter returns a fresh array each pass; without this the buttons — and the
   *  <iconify-icon> elements inside them — are torn down and rebuilt on every change
   *  detection run, so the icons never finish loading. */
  trackMode = (_: number, m: [FulfillmentMode, string, string]) => m[0];

  readonly addrLabels: ['apartment' | 'villa' | 'office', string, string][] = [
    ['apartment', 'Apartment', 'solar:home-2-bold'],
    ['villa', 'Villa', 'solar:home-angle-bold'],
    ['office', 'Office', 'solar:buildings-2-bold'],
  ];

  mode: FulfillmentMode = 'delivery';
  branchId = '';
  addr = '';

  realAddresses: Address[] = [];
  areas: Area[] = [];

  adding = false;
  editingId: string | null = null;
  addrBusy = false;
  addrErr = '';
  aLabel: 'apartment' | 'villa' | 'office' = 'apartment';
  aSubArea = '';
  aBuilding = '';
  aStreet = '';
  aFloor = '';
  aApt = '';
  aNotes = '';
  aPhone = '';
  // map pin
  aLat = 0;
  aLng = 0;
  aServed: boolean | null = null;
  aServedLabel = '';
  checkingLoc = false;

  private sub?: Subscription;

  constructor(
    private data: DataService,
    private session: SessionService,
    private ui: UiService,
    private config: ConfigService,
    private sf: StorefrontService,
    private account: AccountService,
    private branchSvc: BranchService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private i18n: I18nService,
  ) {}

  /** Close the sheet and open the table-QR scanner. */
  scanTable(): void {
    if (this.gateGuest('Log in to start a table order')) return;
    this.ui.closeFulfillment();
    this.router.navigate(['/dinein']);
  }

  ngOnInit(): void {
    this.data.getBrand().subscribe((b) => (this.brand = b));
    this.sub = this.ui.fulfillOpen$.subscribe((o) => {
      if (o && !this.open) this.onOpen();
      this.open = o;
      this.cdr.markForCheck();
    });
    // Re-render when branches reload (e.g. after location grants nearest/distances).
    this.sub.add(this.branchSvc.currentId$.subscribe(() => this.cdr.markForCheck()));
  }
  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  private onOpen(): void {
    const supported = this.config.supportedOrderTypes;
    // An opener that named a mode (e.g. "Scan to dine-in") wins over the saved one.
    const want = this.ui.takeFulfillmentMode() ?? this.session.fulfillment;
    this.mode = supported.includes(want) ? want : (supported[0] || 'delivery');
    this.branchId = this.session.branch.id;
    this.adding = false; this.editingId = null; this.addrErr = '';
    this.addr = this.session.addressId || '';
    // Ask for the device location so branch distances can be shown (server distances
    // need the lat/lng headers; we also compute them client-side as a fallback).
    this.ensureLocation();
    if (this.loggedIn) {
      this.loadAddresses();
      if (!this.areas.length) this.account.getAreas().subscribe({ next: (a) => { this.areas = a; this.cdr.markForCheck(); }, error: () => {} });
    }
  }

  /** Capture the device location once, then reload branches so distances resolve. */
  private ensureLocation(): void {
    if (this.session.coords || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.session.setCoords(pos.coords.latitude, pos.coords.longitude);
        this.branchSvc.load(); // re-fetch with X-Latitude/X-Longitude → server distances
        this.cdr.markForCheck();
      },
      () => { /* denied — client-side haversine still covers branches with coords */ },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  /** Haversine distance (km, 1 dp) from the device to a branch; undefined if unknown. */
  private distanceKm(lat: number | null, lng: number | null): number | undefined {
    const c = this.session.coords;
    if (!c || lat == null || lng == null) return undefined;
    const R = 6371, toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat - c.lat), dLng = toRad(lng - c.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(c.lat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
    return Math.round(2 * R * Math.asin(Math.sqrt(a)) * 10) / 10;
  }

  get loggedIn(): boolean { return this.session.loggedIn; }

  private loadAddresses(): void {
    this.account.getAddresses(this.session.coords?.lat, this.session.coords?.lng).subscribe({
      next: (list) => {
        this.realAddresses = list;
        if (!this.addr && list.length) this.addr = (list.find((a) => a.is_default) || list[0]).id;
        this.cdr.markForCheck();
      },
      error: () => {},
    });
  }

  // ── delivery addresses ──
  get deliveryViews(): AddrView[] {
    if (this.loggedIn) {
      return this.realAddresses.map((a) => ({
        id: a.id, label: a.label, real: true, isDefault: a.is_default, distanceKm: a.distance_km,
        branchId: a.branch_id, line: [a.building, a.street].filter(Boolean).join(' ') + (a.apt_no ? ` · ${a.apt_no}` : ''),
      }));
    }
    // Guests have no saved addresses — they must sign in / sign up to add one.
    return [];
  }

  // ── pickup / dine-in branches (real /branches, nearest → farthest) ──
  get branchViews(): BranchView[] {
    if (this.branchSvc.loaded) {
      const views = this.branchSvc.branchesFor(this.mode).map((b) => {
        const ot = b.order_types.find((o) => mapOrderType(o.order_type) === this.mode);
        // Prefer the server distance; else compute it from the device + branch coords.
        const dist = b.distance_km ?? this.distanceKm(b.latitude, b.longitude);
        return {
          id: b.id, name: b.name, eta: ot?.estimated_minutes ?? null, scheduling: ot?.scheduling_enabled ?? false,
          address: b.address, dist: dist ?? undefined, open: b.status.is_open, opensAt: b.status.opens_at, nearest: b.is_nearest,
        };
      });
      // Nearest-first when we have distances, and flag the closest if the server didn't.
      const anyDist = views.some((v) => v.dist != null);
      if (anyDist) {
        views.sort((a, b) => (a.dist ?? Number.MAX_SAFE_INTEGER) - (b.dist ?? Number.MAX_SAFE_INTEGER));
        if (!views.some((v) => v.nearest) && views[0].dist != null) views[0].nearest = true;
      }
      return views;
    }
    // fallback: bundled mock branches with names/addresses/status
    const sorted = [...(this.brand?.branches ?? [])].sort((a, b) => a.dist - b.dist);
    return sorted.map((b, i) => ({
      id: b.id, name: b.name, eta: b.eta, scheduling: false,
      address: b.address, dist: b.dist, open: b.open, opensAt: b.opensAt, nearest: i === 0,
    }));
  }

  statusLabel(b: BranchView): string {
    if (b.open === undefined) return '';
    return b.open ? 'Open' : (b.opensAt ? `${this.i18n.t('Closed')} · ${this.opensLabel(b.opensAt)}` : 'Closed');
  }
  /** "Opens tomorrow at 1:00 PM" — the API's ISO time, readable and localised. */
  opensLabel(opensAt: string | null | undefined): string { return this.branchSvc.opensLabel(opensAt); }

  get subAreaOptions(): { id: string; name: string }[] {
    return this.areas.flatMap((a) => a.sub_areas.map((s) => ({ id: s.id, name: a.name ? `${a.name} — ${s.name}` : s.name })));
  }
  get needsFloor(): boolean { return this.aLabel === 'apartment' || this.aLabel === 'office'; }
  get addrFormTitle(): string { return this.editingId ? 'Edit address' : 'Add new address'; }

  close(): void { this.ui.closeFulfillment(); }

  /** Guest chose delivery — send them through login/sign-up before adding an address. */
  loginToAdd(): void {
    this.ui.closeFulfillment();
    this.ui.requireLogin('Log in to add a delivery address');
  }

  /**
   * Guests can look but not choose: any change to the order type or branch sends
   * them to sign-in first, so the selection is always made against an account.
   * Returns true when the gate fired and the caller should stop.
   */
  private gateGuest(reason = 'Log in to choose your order type'): boolean {
    if (this.loggedIn) return false;
    this.ui.closeFulfillment();
    this.ui.requireLogin(reason);
    return true;
  }

  selectMode(m: FulfillmentMode): void {
    if (this.gateGuest()) return;
    this.mode = m;
    this.adding = false;
  }
  selectBranch(id: string): void {
    if (this.gateGuest('Log in to pick a branch')) return;
    this.branchId = id;
  }

  // ── real address CRUD (logged-in delivery) ──
  startAdd(): void {
    this.adding = true; this.editingId = null; this.addrErr = '';
    this.aLabel = 'apartment'; this.aSubArea = this.subAreaOptions[0]?.id || '';
    this.aBuilding = ''; this.aStreet = ''; this.aFloor = ''; this.aApt = ''; this.aNotes = ''; this.aPhone = '';
    this.aLat = 0; this.aLng = 0; this.aServed = null; this.aServedLabel = ''; this.checkingLoc = false;
  }

  /** Map pin dropped → resolve the serving sub-area / branch (or flag out-of-zone). */
  onPickLocation(loc: { lat: number; lng: number }): void {
    this.aLat = loc.lat; this.aLng = loc.lng;
    this.checkingLoc = true; this.aServed = null; this.addrErr = '';
    this.cdr.markForCheck();
    this.account.checkLocation(loc.lat, loc.lng).subscribe({
      next: (r) => {
        this.checkingLoc = false;
        this.aServed = r.supported;
        if (r.supported) {
          if (r.subAreaId) this.aSubArea = r.subAreaId;
          this.aServedLabel = [r.subAreaName, r.branchName].filter(Boolean).join(' · ');
        } else { this.aServedLabel = ''; }
        this.cdr.markForCheck();
      },
      error: () => { this.checkingLoc = false; this.aServed = false; this.aServedLabel = ''; this.cdr.markForCheck(); },
    });
  }
  startEdit(a: AddrView): void {
    const src = this.realAddresses.find((x) => x.id === a.id);
    if (!src) return;
    this.adding = true; this.editingId = src.id; this.addrErr = '';
    this.aLabel = (src.label as any) || 'apartment';
    this.aSubArea = src.sub_area_id || this.subAreaOptions[0]?.id || '';
    this.aBuilding = src.building || ''; this.aStreet = src.street || '';
    this.aFloor = src.floor_number || ''; this.aApt = src.apt_no || '';
    this.aNotes = src.address || ''; this.aPhone = src.phone_number || '';
    // Load this address's own pin. Without it the form kept whatever was pinned
    // last, so the map showed a stale spot — and saving moved the address there.
    this.aLat = src.latitude || 0; this.aLng = src.longitude || 0;
    this.aServed = null; this.aServedLabel = ''; this.checkingLoc = false;
  }

  // ── address-form map ──
  /** Opens on the address being edited; central Cairo for a new one. */
  get mapLat(): number { return this.aLat || MAP_DEFAULT_CENTER.lat; }
  get mapLng(): number { return this.aLng || MAP_DEFAULT_CENTER.lng; }
  /** Auto-locate only while adding. Editing must never move an address on its own. */
  get mapAutoLocate(): boolean { return !this.editingId && !(this.aLat && this.aLng); }
  /** Keep the contact number normalised while typing: no leading 0, max 10 digits. */
  onPhoneInput(v: string, el?: HTMLInputElement): void {
    this.aPhone = normalizeEgPhone(v);
    if (el && el.value !== this.aPhone) el.value = this.aPhone;
  }
  private addressPayload() {
    const phone = normalizeEgPhone(this.aPhone);
    return {
      label: this.aLabel, address: this.aNotes.trim() || null,
      building: this.aBuilding.trim(), street: this.aStreet.trim(),
      floor_number: this.needsFloor ? this.aFloor.trim() : undefined,
      apt_no: this.needsFloor ? this.aApt.trim() : undefined,
      // Either the map pin OR a manual sub-area — the server ignores sub_area_id when a pin is sent.
      ...(this.aLat && this.aLng ? { latitude: this.aLat, longitude: this.aLng } : { sub_area_id: this.aSubArea }),
      ...(phone.length === PHONE_DIGITS ? { country_code: '20', phone_number: phone } : {}),
    };
  }
  saveAddress(): void {
    if (this.aServed === false) { this.addrErr = 'This location is outside our delivery area.'; return; }
    if (!this.aBuilding.trim() || !this.aStreet.trim()) { this.addrErr = 'Fill building and street.'; return; }
    if (!(this.aLat && this.aLng) && !this.aSubArea) { this.addrErr = 'Pin your location or pick an area.'; return; }
    this.addrBusy = true; this.addrErr = '';
    const payload = this.addressPayload();
    const req = this.editingId
      ? this.account.updateAddress(this.editingId, payload)
      : this.account.addAddress({ ...payload, is_default: this.realAddresses.length === 0 });
    req.subscribe({
      next: (a) => { this.addrBusy = false; this.adding = false; this.editingId = null; this.addr = a.id; this.loadAddresses(); this.cdr.markForCheck(); },
      error: (e: HttpErrorResponse) => { this.addrBusy = false; this.addrErr = e?.error?.message || 'Could not save the address.'; this.cdr.markForCheck(); },
    });
  }
  deleteAddr(a: AddrView): void {
    this.account.deleteAddress(a.id).subscribe({
      next: () => { if (this.addr === a.id) this.addr = ''; this.loadAddresses(); this.ui.showToast('Address deleted'); this.cdr.markForCheck(); },
      error: () => this.ui.showToast('Could not delete the address'),
    });
  }
  setDefault(a: AddrView): void {
    if (a.isDefault) return;
    this.account.setDefaultAddress(a.id).subscribe({ next: () => { this.loadAddresses(); this.cdr.markForCheck(); }, error: () => {} });
  }

  confirm(): void {
    // No order type or branch is applied for a guest — sign in first, whatever the mode.
    if (this.gateGuest('Log in to start your order')) return;
    if (this.mode === 'delivery') {
      const a = this.deliveryViews.find((x) => x.id === this.addr) || this.deliveryViews[0];
      if (!a) return;
      // Confirming an address makes it the used/default one.
      if (a.real) this.account.setDefaultAddress(a.id).subscribe({ next: () => {}, error: () => {} });
      // the branch serving this address builds the menu
      this.branchSvc.setCurrent(a.branchId || null);
      const branch = { id: a.branchId || a.id, name: a.label, area: a.line };
      this.ui.applyFulfillment('delivery', branch, a.id);
    } else {
      const b = this.branchViews.find((x) => x.id === this.branchId) || this.branchViews[0];
      if (!b) return;
      this.branchSvc.setCurrent(b.id);
      this.ui.applyFulfillment(this.mode, { id: b.id, name: b.name, area: b.name });
    }
  }
}
